import { Tabs, Button, Modal, Tooltip, Input, AutoComplete, Spin } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { makeApiRequest } from '../../shared/api';
import ContentPage from './content';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Onboard from '../facebot/onboard/index';

const Facebot = () => {
  const [facebotList, setFacebotsList] = useState([]);
  const [selectedFacebotId, setSelectedFacebotId] = useState(null); // Store only the ID
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const { Search } = Input;
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    getAllFaceBots();
  }, []);
  const handleAddClick = (e, facebotId) => {
    e.stopPropagation();
    console.log('Add clicked for facebot:', facebotId);
    setIsModalOpen(true)
  };
  const handleModalOk = () => {
    setIsModalOpen(false);
    setShowOnboardModal(true);
  };
  const onSearch = (value) => {
    console.log('Searching for:', value);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };
  const handleSelect = (value, option) => {
    const selectedFacebot = facebotList.find(
      facebot => facebot.facebot_name === value
    );
    if (selectedFacebot) {
      setSelectedFacebotId(selectedFacebot.facebot_id);
      handleTabChange(selectedFacebot.facebot_id);
    }
  };

  const getAllFaceBots = async() => {
    try {
      setLoading(true);
      const response = await makeApiRequest("GET", `facebots?cacheBust=${Date.now()}`);

      if (response && Array.isArray(response)) {
        setFacebotsList(response);
        if (!selectedFacebotId && response.length > 0) {
          setSelectedFacebotId(response[0].facebot_id);
        }
      } else {
        console.error("API response data is not an array:", response);
        setFacebotsList([]);
        setSelectedFacebotId(null);
      }
    } catch (err) {
      console.error("Error fetching facebots:", err);
      setFacebotsList([]);
      setSelectedFacebotId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (activeKey) => {
    setSelectedFacebotId(activeKey);
  };

  // Sort facebotList alphabetically by trimmed facebot_name (case-insensitive, with fallback for missing names)
  const sortedFacebotList = [...facebotList].sort((a, b) =>
    ((a.facebot_name || '').trim().toLowerCase()).localeCompare((b.facebot_name || '').trim().toLowerCase())
  );

  // Use only the name for the dropdown label, no parentheses or IDs
  const autoCompleteOptions = sortedFacebotList.map(facebot => ({
    value: String(facebot.facebot_id),
    label: facebot.facebot_name,
    displayName: facebot.facebot_name,
  }));

  const tabItems = useMemo(() => {
    if (!facebotList || facebotList.length === 0) {
      return [{
        key: 'add-tab',
        label: (
          <Tooltip title="Create a new facebot">
            <Button 
              type="text" 
              icon={<PlusOutlined />} 
              size="small"
              onClick={(e) => handleAddClick(e)}
            />
          </Tooltip>
        ),
        children: null,
      }];
    }
  
    const validFacebots = sortedFacebotList.filter(facebot => 
      facebot && 
      facebot.facebot_id && 
      facebot.facebot_name &&
      facebot.kb_id
    );
    
    return [
      ...validFacebots.map((facebot) => ({
        key: String(facebot.facebot_id),
        label: facebot.facebot_name,
        children: (
          <ContentPage
            facebotId={facebot.facebot_id}
            knowledgeBaseId={facebot.kb_id}
          />
        ),
      })),
      {
        key: 'add-tab',
        label: (
          <Tooltip title="Create a new facebot">
            <Button 
              type="text" 
              icon={<PlusOutlined />} 
              size="small"
              onClick={(e) => handleAddClick(e)}
            />
          </Tooltip>
        ),
        children: null,
      }
    ];
  }, [facebotList, sortedFacebotList]);

  const defaultActiveKey = facebotList.length > 0 ? String(facebotList[0].facebot_id) : undefined;


  return (
    <div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <AutoComplete
              placeholder="Find facebot"
              allowClear
              style={{
                width: 200,
                marginRight: 16,
              }}
              options={autoCompleteOptions}
              value={searchText}
              onChange={setSearchText}
              onSelect={(value) => {
                setSelectedFacebotId(String(value));
                const selectedFacebot = sortedFacebotList.find(
                  (fb) => String(fb.facebot_id) === String(value)
                );
                if (selectedFacebot) {
                  setSearchText(selectedFacebot.facebot_name);
                }
              }}
              filterOption={(inputValue, option) =>
                option?.displayName
                  ?.toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
              notFoundContent={
                <span style={{ color: "#888" }}>No results found</span>
              }
              optionLabelProp="label"
              dropdownRender={(menu) => menu}
            />
          </div>
          <Tabs
            defaultActiveKey={defaultActiveKey}
            activeKey={selectedFacebotId}
            onChange={setSelectedFacebotId}
            type="card"
            items={tabItems}
          />
          <Modal
            title="Create New Facebot"
            open={isModalOpen}
            onOk={handleModalOk}
            onCancel={handleModalCancel}
            okText="Yes"
            cancelText="No"
          >
            <p>Are you sure you want to create one more facebot?</p>
          </Modal>
          <Modal
            title="Create Facebot"
            open={showOnboardModal}
            onCancel={() => setShowOnboardModal(false)}
            footer={null}
            width={1200}
            style={{ top: 20 }}
            bodyStyle={{
              maxHeight: "80vh",
              overflow: "auto",
              padding: 0,
            }}
          >
            <Onboard />
          </Modal>
        </>
      )}
    </div>
  );
};

export default Facebot;